"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Brain, ClipboardList, Clock, Mic, Target } from "lucide-react";
import type { MiniLesson, LessonOutlineSection, ReadAloudLine } from "@/data/mini-lessons";

interface MiniLessonViewerProps {
  miniLesson: MiniLesson;
  onBack: () => void;
}

function OutlineSectionCard({ section }: { section: LessonOutlineSection }) {
  return (
    <Card className="border-[var(--mindup-navy)]/10 bg-white shadow-sm overflow-hidden">
      <CardHeader className="pb-2 pt-4 px-4">
        <CardTitle className="font-display text-base flex items-center gap-2 text-[var(--mindup-navy)]">
          <span className="flex w-6 h-6 items-center justify-center rounded-full bg-[var(--mindup-pink)]/20 text-[var(--mindup-plum)] text-xs font-bold shrink-0">
            {section.number}
          </span>
          {section.title}
          {section.duration && (
            <span className="text-xs font-normal text-[var(--mindup-soft-black)]/70 ml-auto">
              {section.duration}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 px-4 pb-4">
        <div className="space-y-3">
          {section.parts?.map((part, i) => (
            <div key={i}>
              {part.label && (
                <span className="font-semibold text-sm text-[var(--mindup-navy)] block mb-0.5">
                  {part.label}:
                </span>
              )}
              <p className="text-sm text-[var(--mindup-soft-black)] leading-relaxed">
                {part.text}
              </p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function MiniLessonViewer({ miniLesson, onBack }: MiniLessonViewerProps) {
  const { content } = miniLesson;

  return (
    <div className="h-full overflow-auto p-4 space-y-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        className="mb-2 text-white hover:text-white hover:bg-white/20"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to lessons
      </Button>

      <div className="space-y-2">
        <h1 className="font-display text-2xl font-bold tracking-tight text-white">
          {miniLesson.title}
        </h1>
        <div className="flex flex-wrap gap-3 text-sm text-white/90">
          {content?.gradeLevel && (
            <span className="flex items-center gap-1.5">
              <Target className="w-4 h-4" />
              {content.gradeLevel}
            </span>
          )}
          {(content?.estimatedTime || miniLesson.duration) && (
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {content?.estimatedTime ?? miniLesson.duration}
            </span>
          )}
        </div>
      </div>

      {miniLesson.video && (
        <div className="rounded-xl overflow-hidden bg-black/20 aspect-video shadow-lg">
          <video
            src={miniLesson.video}
            controls
            className="w-full h-full"
            preload="metadata"
          >
            Your browser does not support the video tag.
          </video>
        </div>
      )}

      {/* Content — below video */}
      {content && (
        <div className="space-y-4 pt-2">
          {content.purpose && (
            <Card className="border-[var(--mindup-mint)]/40 bg-white shadow-sm">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="font-display text-base flex items-center gap-2 text-[var(--mindup-navy)]">
                  <Brain className="w-4 h-4 text-[var(--mindup-pink)] shrink-0" />
                  Purpose
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-4 pb-4">
                <p className="text-sm text-[var(--mindup-soft-black)] leading-relaxed">
                  {content.purpose}
                </p>
              </CardContent>
            </Card>
          )}

          {content.preparationMaterials && content.preparationMaterials.length > 0 && (
            <Card className="border-[var(--mindup-navy)]/10 bg-white shadow-sm">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="font-display text-base flex items-center gap-2 text-[var(--mindup-navy)]">
                  <ClipboardList className="w-4 h-4 text-[var(--mindup-deep-green)] shrink-0" />
                  Preparation &amp; Materials
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-4 pb-4">
                <ul className="space-y-2 text-sm text-[var(--mindup-soft-black)] leading-relaxed">
                  {content.preparationMaterials.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[var(--mindup-deep-green)] mt-0.5">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {content.readAloudScript && content.readAloudScript.length > 0 && (
            <Card className="border-[var(--mindup-navy)]/10 bg-white shadow-sm">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="font-display text-base flex items-center gap-2 text-[var(--mindup-navy)]">
                  <Mic className="w-4 h-4 text-[var(--mindup-orange)] shrink-0" />
                  Read-Aloud Script
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-4 pb-4 space-y-3">
                {content.readAloudScript.map((line: ReadAloudLine, i) =>
                  line.kind === "direction" ? (
                    <p
                      key={i}
                      className="text-sm font-medium italic text-[var(--mindup-deep-green)] border-l-4 border-[var(--mindup-green)] pl-3 py-1 bg-[var(--mindup-mint)]/40 rounded-r-md"
                    >
                      {line.text}
                    </p>
                  ) : (
                    <div key={i}>
                      <span className="font-semibold text-sm text-[var(--mindup-navy)] block mb-0.5">
                        Teacher Says:
                      </span>
                      <p className="text-sm text-[var(--mindup-soft-black)] leading-relaxed pl-0">
                        &ldquo;{line.text}&rdquo;
                      </p>
                    </div>
                  )
                )}
              </CardContent>
            </Card>
          )}

          {content.keyConcepts && content.keyConcepts.length > 0 && (
            <Card className="border-[var(--mindup-navy)]/10 bg-white shadow-sm">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="font-display text-base text-[var(--mindup-navy)]">
                  Key Concepts
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 px-4 pb-4">
                <ul className="space-y-2 text-sm text-[var(--mindup-soft-black)] leading-relaxed">
                  {content.keyConcepts.map((concept, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[var(--mindup-pink)] mt-0.5">•</span>
                      {concept}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {content.lessonOutline?.map((section) => (
            <OutlineSectionCard key={section.number} section={section} />
          ))}

          {/* Legacy: overview + sections */}
          {content.overview && !content.lessonOutline && (
            <Card className="border-[var(--mindup-mint)]/40 bg-white shadow-sm">
              <CardContent className="pt-4 pb-4 px-4">
                <p className="text-sm text-[var(--mindup-soft-black)] leading-relaxed">
                  {content.overview}
                </p>
              </CardContent>
            </Card>
          )}
          {content.sections?.map((section, i) => (
            <Card key={i} className="border-[var(--mindup-navy)]/10 bg-white shadow-sm">
              <CardHeader className="pb-2 pt-4 px-4">
                <CardTitle className="font-display text-base text-[var(--mindup-navy)]">
                  {section.title}
                </CardTitle>
                {section.intro && (
                  <p className="text-sm text-[var(--mindup-soft-black)] mt-1">
                    {section.intro}
                  </p>
                )}
              </CardHeader>
              <CardContent className="pt-0 px-4 pb-4">
                {section.subsections && (
                  <div className="space-y-4">
                    {section.subsections.map((sub, j) => (
                      <div key={j}>
                        <h4 className="font-semibold text-sm text-[var(--mindup-navy)] mb-1">
                          {sub.title}
                        </h4>
                        <p className="text-sm text-[var(--mindup-soft-black)] leading-relaxed">
                          {sub.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
                {section.items && (
                  <ol className="space-y-2 list-decimal list-inside text-sm text-[var(--mindup-soft-black)] leading-relaxed">
                    {section.items.map((item, j) => (
                      <li key={j} className="pl-1">{item}</li>
                    ))}
                  </ol>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
